import { useState } from "react";
import {
  getApiBase,
  getApiToken,
  getMapTileKey,
  setApiBase,
  setApiToken,
  setMapTileKey,
} from "../../shared/api";
import { STORAGE_KEYS, useStorage } from "../../shared/storage";

export function ApiConfigBar() {
  const [showStr, setShowStr] = useStorage(STORAGE_KEYS.apiShowConfig);
  const minimized = showStr === "0";

  const [base, setBase] = useState(getApiBase());
  const [token, setToken] = useState(getApiToken());
  const [tileKey, setTileKey] = useState(getMapTileKey());
  const [status, setStatus] = useState<{ msg: string; error: boolean }>({
    msg: "",
    error: false,
  });

  function save() {
    // The map builds its tile layer once on mount, so a changed tile key only
    // shows up after the widget re-renders (next screen or a page reload).
    const tileKeyChanged = tileKey.trim() !== getMapTileKey();
    setApiBase(base);
    setApiToken(token);
    setMapTileKey(tileKey.trim());
    setStatus({
      msg: tileKeyChanged
        ? "Saved. Cache cleared — reload to apply the map tile key."
        : "Saved. Cache cleared.",
      error: false,
    });
    window.dispatchEvent(new CustomEvent("conny-api-config-updated"));
  }

  async function test() {
    setStatus({ msg: "Testing…", error: false });
    try {
      const url = `${getApiBase().replace(/\/+$/, "")}/api/v1/public/landlords`;
      const headers: Record<string, string> = { Accept: "application/json" };
      if (getApiToken()) headers["X-Funnel-Key"] = getApiToken();
      const resp = await fetch(url, { headers, credentials: "omit" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setStatus({
        msg: `OK — ${Array.isArray(data) ? data.length : 0} landlords reachable.`,
        error: false,
      });
    } catch (e: unknown) {
      setStatus({ msg: `Failed: ${(e as Error).message}`, error: true });
    }
  }

  return (
    <div className={`cn-api-config ${minimized ? "cn-api-config--min" : ""}`}>
      <div className="cn-api-config__hdr">
        <span className="cn-api-config__title">Conny API config</span>
        <button
          type="button"
          className="cn-api-config__toggle"
          onClick={() => setShowStr(minimized ? "1" : "0")}
        >
          {minimized ? "show" : "hide"}
        </button>
      </div>
      {!minimized && (
        <>
          <div className="cn-api-config__row">
            <label htmlFor="cn-api-base">Base URL</label>
            <input
              id="cn-api-base"
              type="text"
              placeholder="http://localhost:8000"
              value={base}
              onChange={(e) => setBase(e.target.value)}
            />
          </div>
          <div className="cn-api-config__row">
            <label htmlFor="cn-api-token">X-Funnel-Key (optional)</label>
            <input
              id="cn-api-token"
              type="text"
              placeholder="leave empty if endpoint is open"
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </div>
          <div className="cn-api-config__row">
            <label htmlFor="cn-map-tile-key">Map tile key (optional)</label>
            <input
              id="cn-map-tile-key"
              type="text"
              placeholder="sent as ?key= on basemap tile requests"
              value={tileKey}
              onChange={(e) => setTileKey(e.target.value)}
            />
          </div>
          <div className="cn-api-config__actions">
            <button
              type="button"
              className="cn-api-config__btn cn-api-config__btn--primary"
              onClick={save}
            >
              Save
            </button>
            <button
              type="button"
              className="cn-api-config__btn cn-api-config__btn--ghost"
              onClick={test}
            >
              Test
            </button>
          </div>
          <div
            className="cn-api-config__status"
            style={status.error ? { color: "#B5391F" } : undefined}
          >
            {status.msg}
          </div>
        </>
      )}
    </div>
  );
}
